"use client";

import { useState, useEffect, useRef } from "react";
import { MediaAsset } from "@/types/menu";
import Image from "next/image";
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronLeft,
  ChevronRight,
  X,
  FileText,
  PencilIcon,
  MoreVertical,
  Trash,
  Image as ImageIcon,
  Upload,
  Star,
  FolderOpenIcon,
  Volume2,
  VolumeX,
} from "lucide-react";
import dynamic from "next/dynamic";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useToast } from "@/hooks/use-toast";
import useMenuStore from "@/store/menuStore";
import { createClient } from "@/lib/supabase/client";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { pdfjs } from "react-pdf";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";

// Array of RTL languages
const RTL_LANGUAGES = ["arabic", "persian", "hebrew", "urdu"];

// Function to determine if a language is RTL
function isRTLLanguage(language: string): boolean {
  return RTL_LANGUAGES.includes(language);
}

// Define the props interface for PDFViewer
interface PDFViewerProps {
  url: string;
  useScrollableView?: boolean;
  showThumbnails?: boolean;
  onError?: (error: Error) => void;
}

// Create a proper component for PDF loading state
function PDFLoadingState() {
  const t = useTranslations();
  return (
    <div className="flex items-center justify-center h-full">
      <p>{t("MediaViewer.loadingPdfViewer")}</p>
    </div>
  );
}

// Dynamically import the PDFViewer to avoid SSR issues
const PDFViewer = dynamic<PDFViewerProps>(
  () => import("@/components/menu/PDFViewer"),
  {
    ssr: false,
    loading: () => <PDFLoadingState />,
  }
);

// Dynamically import the MediaUploader
const MediaUploader = dynamic(() => import("@/components/menu/MediaUploader"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  ),
});

// Dynamically import the MediaManager
const MediaManager = dynamic(() => import("@/components/menu/MediaManager"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  ),
});

interface MediaCarouselViewerProps {
  mediaAssets: MediaAsset[];
  startIndex: number;
  isOpen: boolean;
  onClose: () => void;
  menuId?: string; // Optional menuId for saving changes
  isPublicView?: boolean; // Add flag to identify if this is a public view
  language?: string; // Add language prop for RTL detection
}

// Initialize PDF.js worker once at module level
if (typeof window !== "undefined") {
  // Ensure we only initialize the worker once
  if (!pdfjs.GlobalWorkerOptions.workerPort) {
    pdfjs.GlobalWorkerOptions.workerSrc = `/pdf/pdf.worker.min.js`;
  }
}

function ErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: Error;
  resetErrorBoundary: () => void;
}) {
  const t = useTranslations();
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <FileText className="h-16 w-16 text-muted-foreground mb-4" />
      <p className="text-destructive font-medium mb-2">
        {t("MediaViewer.errorDisplayingMedia")}
      </p>
      <p className="text-sm text-muted-foreground mb-4">{error.message}</p>
      <Button onClick={resetErrorBoundary} variant="outline">
        {t("MediaViewer.tryAgain")}
      </Button>
    </div>
  );
}

export default function MediaCarouselViewer({
  mediaAssets,
  startIndex,
  isOpen,
  onClose,
  menuId,
  isPublicView = false, // Default to false for backward compatibility
  language, // Language prop for RTL detection
}: MediaCarouselViewerProps) {
  const t = useTranslations("MediaViewer");
  const tUploader = useTranslations("MediaUploader");
  // Safely initialize with a valid startIndex
  const safeStartIndex =
    Array.isArray(mediaAssets) &&
    mediaAssets.length > 0 &&
    startIndex >= 0 &&
    startIndex < mediaAssets.length
      ? startIndex
      : 0;

  const [currentIndex, setCurrentIndex] = useState(safeStartIndex);
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isMediaManagerOpen, setIsMediaManagerOpen] = useState(false);
  const [newFilename, setNewFilename] = useState<string>("");
  const [videoMuted, setVideoMuted] = useState(true);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  // Track which PDFs should be mounted
  const [mountedPdfIndices, setMountedPdfIndices] = useState<Set<number>>(
    new Set()
  );

  // Determine if the current language is RTL
  const isRTL = language ? isRTLLanguage(language) : false;

  const videoRef = useRef<HTMLVideoElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const updateMediaAsset = useMenuStore((state) => state.updateMediaAsset);
  const setDefaultMediaId = useMenuStore((state) => state.setDefaultMediaId);
  const deleteMediaAsset = useMenuStore((state) => state.deleteMediaAsset);
  const menu = useMenuStore((state) => state.menu);
  const supabase = createClient();

  // Reset current index when startIndex changes, but ensure it's valid
  useEffect(() => {
    if (!Array.isArray(mediaAssets) || mediaAssets.length === 0) {
      return;
    }

    if (startIndex >= 0 && startIndex < mediaAssets.length) {
      setCurrentIndex(startIndex);
    } else if (mediaAssets.length > 0) {
      // Only log warning if there are actually media assets
      console.warn(
        "MediaCarouselViewer: startIndex out of bounds, using index 0 instead"
      );
      setCurrentIndex(0);
    }
  }, [startIndex, mediaAssets]);

  // Reset error state when changing media or closing/opening the viewer
  useEffect(() => {
    setPdfError(null);
    setIsSwiping(false);
    setTouchDelta(0);
  }, [currentIndex, isOpen]);

  // Handle PDF worker cleanup
  useEffect(() => {
    return () => {
      // Cleanup PDF worker when component unmounts
      if (typeof window !== "undefined") {
        const worker = pdfjs.GlobalWorkerOptions.workerPort;
        if (worker) {
          worker.terminate();
          pdfjs.GlobalWorkerOptions.workerPort = null;
        }
      }
    };
  }, []);

  // Handle PDF mounting and cleanup for individual PDFs
  useEffect(() => {
    if (!isOpen) {
      // Clear mounted PDFs when viewer closes
      setMountedPdfIndices(new Set());
      return;
    }

    const currentAsset = mediaAssets[currentIndex];

    // Only manage PDF mounting if current asset is a PDF
    if (currentAsset?.type === "pdf") {
      const newMountedIndices = new Set<number>([currentIndex]);

      // Preload adjacent PDFs
      if (currentIndex > 0 && mediaAssets[currentIndex - 1]?.type === "pdf") {
        newMountedIndices.add(currentIndex - 1);
      }
      if (
        currentIndex < mediaAssets.length - 1 &&
        mediaAssets[currentIndex + 1]?.type === "pdf"
      ) {
        newMountedIndices.add(currentIndex + 1);
      }

      setMountedPdfIndices(newMountedIndices);
    } else {
      // If current asset is not a PDF, unmount all PDFs
      setMountedPdfIndices(new Set());
    }
  }, [currentIndex, mediaAssets, isOpen]);

  // Add effect to handle video playback when index changes
  useEffect(() => {
    const currentAsset = mediaAssets[currentIndex];
    const videoElement = videoRef.current;

    if (currentAsset?.type === "video" && videoElement) {
      console.log("Attempting to play video:", currentAsset.url);

      // Start playing when a video comes into view
      videoElement.play().catch((error) => {
        console.error("Video playback error:", error);

        // Most browsers require user interaction for unmuted autoplay
        if (!videoMuted) {
          console.log("Retrying with muted playback");
          setVideoMuted(true);
          videoElement.muted = true;
          videoElement.play().catch((retryError) => {
            console.error("Muted video playback failed:", retryError);
          });
        }
      });
      setIsVideoPlaying(true);
    }

    // Cleanup: pause video when changing to different media
    return () => {
      if (videoElement) {
        console.log("Cleaning up video playback");
        videoElement.pause();
        setIsVideoPlaying(false);
      }
    };
  }, [currentIndex, mediaAssets, videoMuted]);

  // Handle PDF errors with retry logic
  const handlePdfError = (error: Error) => {
    console.error("PDF viewer error:", error);
    setPdfError(error);

    if (
      error.message.includes("Worker was terminated") ||
      error.message.includes("sendWithPromise") ||
      error.message.includes("Cannot read properties of null")
    ) {
      toast({
        title: t("pdfLoadingIssue"),
        description: t("pdfRetrying"),
        variant: "destructive",
      });

      // Reinitialize the worker
      if (typeof window !== "undefined") {
        const worker = pdfjs.GlobalWorkerOptions.workerPort;
        if (worker) {
          worker.terminate();
          pdfjs.GlobalWorkerOptions.workerPort = null;
        }
        pdfjs.GlobalWorkerOptions.workerSrc = `/pdf/pdf.worker.min.js`;
      }

      // Reset mounted PDFs and retry current one
      setMountedPdfIndices(new Set([currentIndex]));
    }
  };

  // Handle touch start event
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwiping(true);
    setTouchDelta(0);
  };

  // Handle touch move event
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;

    const currentX = e.targetTouches[0].clientX;
    setTouchEnd(currentX);

    // Calculate raw delta
    const delta = touchStart - currentX;

    // Determine if we can swipe in this direction
    const canSwipeLeft = currentIndex < mediaAssets.length - 1;
    const canSwipeRight = currentIndex > 0;

    // Apply resistance if swiping beyond bounds
    // We don't invert the direction detection since we want the physical
    // gesture to remain consistent regardless of text direction
    if ((delta > 0 && !canSwipeLeft) || (delta < 0 && !canSwipeRight)) {
      // Resistance effect when at the edges - use 15% of the movement
      setTouchDelta(delta * 0.15);
    } else {
      setTouchDelta(delta);
    }
  };

  // Handle touch end event
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwiping(false);
      return;
    }

    const distance = touchStart - touchEnd;

    // Calculate what percentage of the screen width has been swiped
    const screenWidth = window.innerWidth;
    const swipePercentage = Math.abs(distance) / screenWidth;

    // If user swiped more than 20% of screen width or with enough velocity, navigate
    const isSignificantSwipe = swipePercentage > 0.2;

    // Keep physical direction consistent regardless of language direction
    // This matches how most touch interfaces work - the physical gesture remains the same
    const isLeftSwipe = distance > 0;
    const isRightSwipe = distance < 0;

    // Navigate based on swipe direction if the swipe is significant
    if (
      isLeftSwipe &&
      currentIndex < mediaAssets.length - 1 &&
      isSignificantSwipe
    ) {
      goToNext();
    } else if (isRightSwipe && currentIndex > 0 && isSignificantSwipe) {
      goToPrevious();
    }

    // Reset values
    setIsSwiping(false);
    setTouchStart(null);
    setTouchEnd(null);
    setTouchDelta(0);
  };

  // Navigate to previous asset
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  // Navigate to next asset
  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < mediaAssets.length - 1 ? prevIndex + 1 : prevIndex
    );
  };

  // Get current media asset with safety check
  const currentAsset =
    Array.isArray(mediaAssets) &&
    mediaAssets.length > 0 &&
    currentIndex >= 0 &&
    currentIndex < mediaAssets.length
      ? mediaAssets[currentIndex]
      : null;

  // Early return if no valid asset to display
  if (!currentAsset) return null;

  // Open rename dialog
  const handleRenameClick = () => {
    if (currentAsset) {
      setNewFilename(currentAsset.filename || "");
      setIsRenameDialogOpen(true);
    }
  };

  // Handle rename confirmation
  const handleConfirmRename = async () => {
    if (!menuId || !currentAsset || !newFilename.trim()) return;

    try {
      const updatedAsset = {
        ...currentAsset,
        filename: newFilename.trim(),
      };

      await updateMediaAsset(menuId, updatedAsset, supabase);

      // Update the local mediaAssets array to reflect the change
      const updatedAssets = [...mediaAssets];
      updatedAssets[currentIndex] = updatedAsset;

      toast({
        title: "File renamed",
        description: "The file has been successfully renamed.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error renaming asset:", error);
      toast({
        title: "Rename failed",
        description: "Failed to rename the file. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRenameDialogOpen(false);
    }
  };

  // Handle setting asset as default cover
  const handleSetAsCover = async () => {
    if (!menuId || !currentAsset) return;

    try {
      await setDefaultMediaId(menuId, currentAsset.id, supabase);

      toast({
        title: "Cover updated",
        description: "This media is now set as the default cover.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error setting cover:", error);
      toast({
        title: "Could not set cover",
        description: "Failed to set this as the cover. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Open delete dialog
  const handleDeleteClick = () => {
    if (currentAsset) {
      setIsDeleteDialogOpen(true);
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!menuId || !currentAsset) return;

    try {
      await deleteMediaAsset(menuId, currentAsset.id, supabase);

      // Close the delete dialog
      setIsDeleteDialogOpen(false);

      // If there are more assets, move to the next one or previous one
      if (mediaAssets.length > 1) {
        // If we're deleting the last item, go to the previous one
        if (currentIndex === mediaAssets.length - 1) {
          setCurrentIndex(currentIndex - 1);
        }
        // For all other cases, we can stay at the current index as the next item will slide in
      } else {
        // If this was the last asset, close the viewer
        onClose();
      }

      toast({
        title: "File deleted",
        description: "The file has been successfully deleted.",
        variant: "default",
      });
    } catch (error) {
      console.error("Error deleting asset:", error);
      toast({
        title: "Delete failed",
        description: "Failed to delete the file. Please try again.",
        variant: "destructive",
      });
      setIsDeleteDialogOpen(false);
    }
  };

  // Handle Upload button click
  const handleUploadClick = () => {
    setIsUploadDialogOpen(true);
  };

  // Handle when upload is complete
  const handleUploadComplete = () => {
    // You might want to refresh the media assets here
    toast({
      title: tUploader("uploadComplete"),
      description: tUploader("mediaAvailable"),
      variant: "default",
    });
  };

  // Handle Media Manager button click
  const handleMediaManagerClick = () => {
    setIsMediaManagerOpen(true);
  };

  // Update the toggleMute function to handle play state
  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newMutedState = !videoMuted;
    setVideoMuted(newMutedState);
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
      // If unmuting and video isn't playing, try to play
      if (!newMutedState && !isVideoPlaying) {
        videoRef.current.play().catch(console.error);
        setIsVideoPlaying(true);
      }
    }
  };

  // Function to truncate long filenames with ellipsis in the middle
  const truncateFilename = (filename: string, maxLength: number = 24) => {
    if (!filename || filename.length <= maxLength) return filename;

    // Find the last dot to preserve file extension
    const lastDotIndex = filename.lastIndexOf(".");

    if (lastDotIndex === -1) {
      // No extension, simple truncation
      const half = Math.floor(maxLength / 2);
      return `${filename.substring(0, half)}...${filename.substring(
        filename.length - half
      )}`;
    }

    const extension = filename.substring(lastDotIndex);
    const nameWithoutExt = filename.substring(0, lastDotIndex);

    // Allocate 2/3 of remaining space (after accounting for extension and ellipsis) to the start
    const remainingLength = maxLength - extension.length - 3; // 3 for the ellipsis
    const startLength = Math.floor(remainingLength * 0.67);
    const endLength = remainingLength - startLength;

    if (remainingLength <= 0) {
      // Extension is already too long
      return `${nameWithoutExt.substring(0, 1)}...${extension}`;
    }

    return `${nameWithoutExt.substring(
      0,
      startLength
    )}...${nameWithoutExt.substring(
      nameWithoutExt.length - endLength
    )}${extension}`;
  };

  // Format the filename for display (handles both regular and legacy assets)
  const getDisplayName = (asset: MediaAsset) => {
    // For legacy assets, show a clean name without any "legacy" indicator
    if (asset.id === "legacy-pdf") {
      return "Menu PDF";
    }
    if (asset.id === "legacy-image") {
      return "Menu Image";
    }

    // For regular assets, show the filename or a default name
    return truncateFilename(asset.filename || `Media ${currentIndex + 1}`);
  };

  const isLegacy =
    currentAsset.id === "legacy-pdf" || currentAsset.id === "legacy-image";
  const isDefaultMedia = menu?.defaultMediaId === currentAsset.id;

  // Helper function to determine if PDF should be mounted
  const shouldMountPdf = (index: number) => {
    return mountedPdfIndices.has(index);
  };

  // Wrap the media rendering in an error boundary
  const renderMedia = (asset: MediaAsset, index: number) => {
    if (!asset) return null;

    return (
      <ErrorBoundary
        key={asset.id}
        FallbackComponent={ErrorFallback}
        onReset={() => {
          // Reset error state
          setPdfError(null);
          setVideoMuted(true);
          setIsVideoPlaying(false);
        }}
        resetKeys={[asset.id, index]}
      >
        <div
          className="min-w-full w-full h-full flex items-center justify-center flex-shrink-0"
          data-active={index === currentIndex ? "true" : "false"}
          tabIndex={index === currentIndex ? 0 : -1}
        >
          {asset.type === "image" ? (
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="max-h-[calc(100dvh-100px)] max-w-[calc(100dvw-32px)] relative h-full w-full">
                <Image
                  src={asset.url}
                  alt={getDisplayName(asset)}
                  fill
                  sizes="100vw"
                  className="object-contain"
                  priority={index === currentIndex}
                  loading={index === currentIndex ? "eager" : "lazy"}
                  onError={(e) => {
                    console.error("Image load error:", e);
                  }}
                />
              </div>
            </div>
          ) : asset.type === "video" ? (
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="max-h-[calc(100dvh-100px)] max-w-[calc(100dvw-32px)] relative h-full w-full flex items-center justify-center">
                <video
                  ref={index === currentIndex ? videoRef : undefined}
                  src={asset.url}
                  className="max-h-full max-w-full"
                  controls
                  playsInline
                  loop
                  muted={videoMuted}
                  onPlay={() => setIsVideoPlaying(true)}
                  onPause={() => setIsVideoPlaying(false)}
                  onError={(e) => {
                    console.error("Video load error:", e);
                  }}
                />
                {index === currentIndex && (
                  <button
                    onClick={toggleMute}
                    className={cn(
                      "absolute bottom-4 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer shadow-md",
                      "bg-black/60 text-white backdrop-blur-sm",
                      isRTL ? "left-4" : "right-4"
                    )}
                  >
                    {videoMuted ? (
                      <VolumeX className="h-5 w-5" />
                    ) : (
                      <Volume2 className="h-5 w-5" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : asset.type === "pdf" ? (
            <div className="w-full h-full max-h-[calc(100dvh-100px)]">
              {shouldMountPdf(index) ? (
                index === currentIndex && pdfError ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                    <p className="text-destructive font-medium mb-2">
                      {t("errorLoadingPdf")}
                    </p>
                    <p className="text-sm text-muted-foreground mb-4">
                      {pdfError.message ||
                        "There was a problem displaying this PDF."}
                    </p>
                    <Button
                      onClick={() => {
                        setPdfError(null);
                      }}
                      variant="outline"
                    >
                      {t("tryAgain")}
                    </Button>
                  </div>
                ) : (
                  <PDFViewer url={asset.url} onError={handlePdfError} />
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <FileText className="h-16 w-16 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">{t("pdfPreview")}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <FileText className="h-16 w-16 text-muted-foreground mb-4" />
              <p>{t("unsupportedFileType")}</p>
            </div>
          )}
        </div>
      </ErrorBoundary>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          "max-w-full w-[100dvw] h-[100dvh] p-0 m-0 rounded-none flex flex-col",
          isRTL && "rtl"
        )}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <VisuallyHidden>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </VisuallyHidden>

        <div className="bg-background/95 backdrop-blur-sm p-2 sticky top-0 z-10 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium flex items-center gap-2">
              {getDisplayName(currentAsset)}

              {menuId && !isPublicView && !isLegacy && (
                <div
                  className={cn(
                    "flex items-center gap-1.5",
                    isRTL ? "mr-1" : "ml-1"
                  )}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 p-0.5 hover:bg-muted rounded-full"
                          onClick={handleRenameClick}
                          tabIndex={-1}
                        >
                          <PencilIcon className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                          <span className="sr-only">{t("renameFile")}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p>{t("renameFile")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {!isDefaultMedia &&
                    (currentAsset.type === "image" ||
                      currentAsset.type === "video" ||
                      currentAsset.type === "pdf") && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 p-0.5 hover:bg-muted rounded-full"
                              onClick={handleSetAsCover}
                              tabIndex={-1}
                            >
                              <Star className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                              <span className="sr-only">{t("makeCover")}</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom">
                            <p>{t("makeCover")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                </div>
              )}
            </div>
            {isDefaultMedia && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                      {t("coverLabel")}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("coverImageTooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          <div className="flex items-center gap-2">
            {menuId && !isPublicView && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMediaManagerClick}
                  className={cn(
                    "hidden sm:flex items-center gap-1 bg-muted hover:bg-muted/80 border-muted text-foreground",
                    isRTL && "flex-row-reverse"
                  )}
                  tabIndex={-1}
                >
                  <FolderOpenIcon className="h-4 w-4" />
                  {t("manageButton")}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleUploadClick}
                  className={cn(
                    "hidden sm:flex items-center gap-1 bg-primary hover:bg-primary/90 text-primary-foreground",
                    isRTL && "flex-row-reverse"
                  )}
                  tabIndex={-1}
                >
                  <Upload className="h-4 w-4" />
                  {t("uploadButton")}
                </Button>
              </>
            )}
            <div className="flex items-center gap-1">
              {menuId && !isLegacy && !isPublicView && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      tabIndex={-1}
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">{t("moreOptions")}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align={isRTL ? "start" : "end"}>
                    <DropdownMenuItem
                      onClick={handleRenameClick}
                      className={isRTL ? "flex-row-reverse" : ""}
                    >
                      <PencilIcon
                        className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")}
                      />
                      {t("renameFile")}
                    </DropdownMenuItem>
                    {!isDefaultMedia &&
                      currentAsset &&
                      (currentAsset.type === "image" ||
                        currentAsset.type === "video" ||
                        currentAsset.type === "pdf") && (
                        <DropdownMenuItem
                          onClick={handleSetAsCover}
                          className={isRTL ? "flex-row-reverse" : ""}
                        >
                          <ImageIcon
                            className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")}
                          />
                          {t("makeCover")}
                        </DropdownMenuItem>
                      )}

                    <DropdownMenuSeparator />

                    {/* Only show these items on small screens */}
                    <div className="sm:hidden">
                      <DropdownMenuItem
                        onClick={handleUploadClick}
                        className={isRTL ? "flex-row-reverse" : ""}
                      >
                        <Upload
                          className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")}
                        />
                        {t("uploadFiles")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleMediaManagerClick}
                        className={isRTL ? "flex-row-reverse" : ""}
                      >
                        <FolderOpenIcon
                          className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")}
                        />
                        {t("manageMedia")}
                      </DropdownMenuItem>

                      <DropdownMenuSeparator />
                    </div>

                    <DropdownMenuItem
                      onClick={handleDeleteClick}
                      className={cn(
                        "text-destructive",
                        isRTL && "flex-row-reverse"
                      )}
                    >
                      <Trash
                        className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")}
                      />
                      {t("delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <DialogClose className="rounded-full h-8 w-8 flex items-center justify-center hover:bg-muted">
                <X className="h-4 w-4" />
                <span className="sr-only">{t("close")}</span>
              </DialogClose>
            </div>
          </div>
        </div>

        <div
          ref={contentRef}
          className="flex-grow relative overflow-hidden flex items-center justify-center p-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="absolute inset-0 flex"
            style={{
              transform: `translateX(calc(${
                isRTL ? "" : "-"
              }100% * ${currentIndex} + ${
                isSwiping ? (isRTL ? touchDelta : -touchDelta) : 0
              }px))`,
              transition: isSwiping
                ? "none"
                : "transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
            }}
          >
            {mediaAssets?.map((asset, index) => renderMedia(asset, index))}
          </div>
        </div>

        <div className="p-4 flex justify-between items-center bg-background/95 backdrop-blur-sm">
          <Button
            variant="default"
            size="icon"
            onClick={isRTL ? goToNext : goToPrevious}
            disabled={
              isRTL
                ? currentIndex === mediaAssets.length - 1
                : currentIndex === 0
            }
            className="bg-primary hover:bg-primary/90 rounded-full justify-center"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">{isRTL ? t("next") : t("previous")}</span>
          </Button>

          <div className="flex items-center gap-2">
            <div className="text-sm font-medium">
              {currentIndex + 1} / {mediaAssets.length}
            </div>
          </div>

          <Button
            variant="default"
            size="icon"
            onClick={isRTL ? goToPrevious : goToNext}
            disabled={
              isRTL
                ? currentIndex === 0
                : currentIndex === mediaAssets.length - 1
            }
            className="bg-primary hover:bg-primary/90 rounded-full"
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">{isRTL ? t("previous") : t("next")}</span>
          </Button>
        </div>
      </DialogContent>

      {/* Rename dialog */}
      <Dialog open={isRenameDialogOpen} onOpenChange={setIsRenameDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogTitle>{t("renameDialogTitle")}</DialogTitle>
          <DialogDescription className="text-balance">
            {t("renameDialogDescription")}
          </DialogDescription>
          <div className="py-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (newFilename.trim()) {
                  handleConfirmRename();
                }
              }}
            >
              <Input
                value={newFilename}
                onChange={(e) => setNewFilename(e.target.value)}
                placeholder={t("fileNamePlaceholder")}
                className="w-full"
                autoFocus
              />
              {/* Hidden submit button to enable form submission */}
              <button type="submit" className="hidden" />
            </form>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRenameDialogOpen(false)}
            >
              {t("cancel")}
            </Button>
            <Button
              onClick={handleConfirmRename}
              disabled={!newFilename.trim()}
            >
              {t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("deleteDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("deleteDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          {isDefaultMedia && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4 text-sm text-yellow-700">
              <p className="font-medium">{t("warningLabel")}</p>
              <p>{t("coverDeleteWarning")}</p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              {t("cancel")}
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              {t("delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="p-0 sm:p-6 h-auto w-[100dvw] sm:w-auto sm:min-w-[500px] sm:max-w-[600px] flex flex-col space-y-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-6 pb-2 sm:p-0 sm:pb-2">
            <DialogTitle>{t("uploadDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("uploadDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-grow">
            {menuId && (
              <MediaUploader
                menuId={menuId}
                supabase={supabase}
                onUploadComplete={handleUploadComplete}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0 p-6 pt-4 sm:p-0 mt-auto border-t sm:border-0">
            <Button
              variant="outline"
              onClick={() => setIsUploadDialogOpen(false)}
            >
              {t("closeButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Manager dialog */}
      {menuId && supabase && (
        <Dialog open={isMediaManagerOpen} onOpenChange={setIsMediaManagerOpen}>
          <DialogContent className="p-0 sm:p-6 h-[100dvh] w-[100dvw] sm:h-auto sm:w-auto sm:min-w-[500px] sm:max-w-[600px] sm:max-h-[85vh] flex flex-col space-y-0 overflow-hidden">
            <DialogHeader className="flex-shrink-0 p-6 pb-2 sm:p-0 sm:pb-2">
              <DialogTitle>{t("mediaManager")}</DialogTitle>
              <DialogDescription className="text-balance">
                {t("mediaManagerDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-grow">
              <MediaManager menuId={menuId} supabase={supabase} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
